import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'leadgab-theme';

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function apply(theme: Theme): void {
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

/**
 * `serverTheme` is the account-level preference. A local choice wins — it is
 * the more specific signal, made on this device — but a fresh browser adopts
 * whatever the account has saved instead of falling back to the OS.
 */
export function useTheme(serverTheme?: Theme): { theme: Theme; setTheme: (next: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? serverTheme ?? 'system',
  );

  useEffect(() => {
    apply(theme);

    if (theme !== 'system') return;

    // Only follow the OS while the user hasn't pinned a preference.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (): void => {
      apply('system');
    };

    media.addEventListener('change', listener);
    return () => {
      media.removeEventListener('change', listener);
    };
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);

    setThemeState(next);
  }, []);

  return { theme, setTheme };
}
